import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ScopeGapExplainers } from './ScopeGapExplainers';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ReportIssueButton from '../report/ReportIssueButton';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import {
  Check, X, Link2, Unlink, FileText, Receipt, Wallet, CreditCard,
  TrendingUp, Package, RefreshCw, ArrowRightLeft, Trash2, Loader2,
  Calendar, AlertCircle, Scissors, Info, ChevronDown, ChevronUp,
  ArrowLeft, Landmark, ChevronRight, Download, FileSpreadsheet, Mail
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { SplitTransactionDialog } from './SplitTransactionDialog';
import SmartLinkDocumentModal from './SmartLinkDocumentModal';
import { BankFeedDocumentDialog } from './BankFeedDocumentDialog';
import { TaxPaymentDialog } from './TaxPaymentDialog';
import RecordOtherReceiptModal from '../payments/RecordOtherReceiptModal';
import { useAuth } from '../../App';
import { accountingYearStart } from '../../utils/accountingYear';
import RecordOtherPaymentModal from '../payments/RecordOtherPaymentModal';

import { API_BASE } from '../../utils/apiUrl';
import { InfoTip } from '../ui/info-tip';
import { formatCurrency } from '../../utils/currency';
import { ReconItemsTable, ReconActionButton, DirectionPill } from './ReconItemsTable';
import { BankTxActions } from './BankTxActions';
const API = API_BASE;

// Dialog formatter - always the BANK account's currency (passed down as `currency`), never a hardcoded one.
const formatAmount = (amt, currency) => formatCurrency(Math.abs(parseFloat(amt) || 0), (currency || 'USD').toUpperCase());

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

// ======================== SHARED · source transaction card ========================
function SourceTxCard({ description, date, amount, currency, direction, testId }) {
  const isIn = direction === 'inflow';
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3" data-testid={testId}>
      <div className="flex items-start gap-3 min-w-0">
        <span className={`mt-0.5 shrink-0 inline-flex h-6 items-center rounded-full px-2 text-[10px] font-semibold uppercase tracking-wide ${isIn ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {isIn ? 'Money in' : 'Money out'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 leading-snug break-words line-clamp-2" title={description}>{description || '—'}</p>
          <p className="text-xs text-slate-500 mt-1">{formatDate(date)}</p>
        </div>
        <span className={`shrink-0 text-sm font-semibold tabular-nums ${isIn ? 'text-emerald-700' : 'text-slate-900'}`}>{formatAmount(amount, currency)}</span>
      </div>
    </div>
  );
}

// ======================== MATCH CANDIDATE DIALOG ========================
function MatchCandidateDialog({ open, onClose, candidates, loading, sourceItem, onMatch, side, currency }) {
  if (!open) return null;
  const srcDir = sourceItem?.direction || ((sourceItem?.amount ?? sourceItem?.bank_amount) < 0 ? 'outflow' : 'inflow');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="match-candidate-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle>Select Match</DialogTitle>
          <DialogDescription>
            {side === 'bank'
              ? 'Pick the book entry that this bank transaction settles. Only entries for the exact same amount are shown.'
              : 'Pick the bank transaction that settles this book entry. Only lines for the exact same amount are shown.'}
          </DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-5 space-y-3">
          {sourceItem && (
            <SourceTxCard
              testId="match-source-item"
              description={sourceItem.description || sourceItem.bank_description}
              date={sourceItem.date || sourceItem.bank_date}
              amount={sourceItem.amount ?? sourceItem.bank_amount}
              currency={currency}
              direction={srcDir}
            />
          )}
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {side === 'bank' ? 'Entries in your books' : 'Bank transactions'}
            </p>
            {!loading && <span className="text-[11px] text-slate-500">{candidates.length} candidate{candidates.length === 1 ? '' : 's'}</span>}
          </div>
          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-2" data-testid="match-candidate-list">
            {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
            {!loading && candidates.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-200 py-8 text-center">
                <p className="text-sm text-slate-600">No entry with this exact amount.</p>
                <p className="text-xs text-slate-500 mt-1">Record it instead, or check the amount on the book entry.</p>
              </div>
            )}
            {!loading && candidates.map((c, i) => (
              <button
                key={c.id || i}
                type="button"
                onClick={() => onMatch(c)}
                className="group w-full min-w-0 text-left rounded-lg border border-slate-200 bg-white px-3.5 py-3 transition-colors hover:border-[#283F8B]/50 hover:bg-[#283F8B]/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#283F8B]/40"
                data-testid={`match-candidate-${i}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 leading-snug break-words line-clamp-2" title={c.description || c.source_document_ref || 'Entry'}>
                      {c.description || c.source_document_ref || 'Entry'}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                      <span>{formatDate(c.date)}</span>
                      {c.source_type && (
                        <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium capitalize text-slate-600">
                          {c.source_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      {c.source_document_ref && c.description && (
                        <span className="font-mono text-[10px] text-slate-400 truncate max-w-[160px]">{c.source_document_ref}</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold tabular-nums text-slate-900">{formatAmount(c.amount, currency)}</p>
                    <p className="text-[10px] font-medium text-[#283F8B] opacity-0 group-hover:opacity-100 transition-opacity">Match →</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ======================== RECORD DOCUMENT TYPE DIALOG ========================
function RecordOption({ icon: Icon, title, subtitle, tone, onClick, testId, className = '', dashed = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group flex w-full min-w-0 items-start gap-3 rounded-xl border bg-white px-4 py-3.5 text-left transition-all hover:-translate-y-px hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#283F8B]/40 ${dashed ? 'border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/60' : `border-slate-200 ${tone.hover}`} ${className}`}
    >
      <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.bg}`}>
        <Icon className={`h-[18px] w-[18px] ${tone.text}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-snug text-slate-500 whitespace-normal break-words">{subtitle}</span>
      </span>
      <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" />
    </button>
  );
}

const TONES = {
  green: { bg: 'bg-emerald-50', text: 'text-emerald-600', hover: 'hover:border-emerald-300' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', hover: 'hover:border-blue-300' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', hover: 'hover:border-orange-300' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', hover: 'hover:border-purple-300' },
  navy: { bg: 'bg-[#283F8B]/10', text: 'text-[#283F8B]', hover: 'hover:border-[#283F8B]/40' },
  sky: { bg: 'bg-sky-50', text: 'text-sky-600', hover: 'hover:border-sky-300' },
};

function RecordDocumentTypeDialog({ open, onClose, transaction, onNavigate, currency }) {
  if (!open || !transaction) return null;
  const direction = transaction.direction || (transaction.amount > 0 ? 'inflow' : 'outflow');
  const isIn = direction === 'inflow';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="record-transaction-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle>Record Transaction</DialogTitle>
          <DialogDescription>Choose how this bank transaction should be recorded in your books.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <SourceTxCard
            testId="record-source-item"
            description={transaction.description}
            date={transaction.date}
            amount={transaction.amount}
            currency={currency}
            direction={direction}
          />
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Create a new document</p>
            <div className="grid grid-cols-1 gap-2.5">
              {isIn ? (
                <>
                  <RecordOption icon={Receipt} tone={TONES.green} title="Invoice" subtitle="Customer payment received"
                    onClick={() => onNavigate('invoice', transaction)} testId="record-as-invoice" />
                  <RecordOption icon={Wallet} tone={TONES.blue} title="Other Receipt" subtitle="Loan, capital, refund, etc."
                    onClick={() => onNavigate('other_receipt', transaction)} testId="record-as-other-receipt" />
                </>
              ) : (
                <>
                  <RecordOption icon={FileText} tone={TONES.orange} title="Bill" subtitle="Vendor payment made"
                    onClick={() => onNavigate('bill', transaction)} testId="record-as-bill" />
                  <RecordOption icon={Wallet} tone={TONES.purple} title="Other Payment" subtitle="Salary, bank charge, etc."
                    onClick={() => onNavigate('other_payment', transaction)} testId="record-as-other-payment" />
                </>
              )}
            </div>
            {!isIn && (
              <RecordOption icon={Landmark} tone={TONES.navy} title="Income tax payment"
                subtitle="Settle federal / state tax payable with the EFTPS or state-portal reference"
                onClick={() => onNavigate('tax_payment', transaction)} testId="record-as-tax-payment" />
            )}
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Or use something already in your books</p>
            <RecordOption icon={Link2} tone={TONES.sky} dashed title="Link to existing transaction"
              subtitle={isIn
                ? 'Open invoice, investment income, asset sale, deferred revenue, loan drawdown…'
                : 'Open bill, loan repayment, prepayment, investment purchase, asset purchase…'}
              onClick={() => onNavigate('link_existing', transaction)} testId="record-link-existing" />
          </div>
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <Info className="h-3.5 w-3.5" /> The form opens pre-filled with this transaction's details
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ======================== DELETE CONFIRMATION DIALOG ========================
function DeleteConfirmDialog({ open, onClose, item, onConfirm, loading, currency }) {
  if (!open || !item) return null;
  const isInvoiceOrBill = item.source_type?.includes('invoice') || item.source_type?.includes('bill');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Delete Book Entry
          </DialogTitle>
          <DialogDescription>
            {isInvoiceOrBill
              ? 'This will remove the payment from the invoice/bill and reverse related journal entries. The document itself will not be deleted.'
              : 'This will delete the document and reverse all related journal entries.'}
          </DialogDescription>
        </DialogHeader>
        <div className="p-3 bg-red-50 rounded-lg space-y-1">
          <p className="text-sm font-medium">{item.description}</p>
          <div className="flex justify-between text-xs">
            <span>{formatDate(item.date)}</span>
            <span className="font-semibold">{formatAmount(item.amount, currency)}</span>
          </div>
          {item.source_type && <Badge variant="outline" className="text-[10px]">{item.source_type.replace(/_/g, ' ')}</Badge>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="destructive" onClick={() => onConfirm(item)} disabled={loading} data-testid="confirm-delete-gl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Delete & Reverse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================== MAIN COMPONENT ========================
export default function BankReconciliationCenter({ bankAccountId, onClose, onRefresh }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asAtDate, setAsAtDate] = useState('');
  const [exporting, setExporting] = useState(null);
  const { business } = useAuth() || {};
  // Export period for the matched-items listing: accounting-year-to-date by default.
  const [exportFromDate, setExportFromDate] = useState(() => accountingYearStart(business));
  useEffect(() => { setExportFromDate(accountingYearStart(business)); }, [business?.accounting_start_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailReconPack = async () => {
    setExporting('pack');
    try {
      const res = await axios.post(`${API}/bank-reconciliation/recon-pack/send-now`);
      const d = res.data || {};
      toast.success(`Reconciliation pack for ${d.period || 'last month'} emailed · ${d.attachment_count || 0} PDF(s) to ${d.recipient_count || 0} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not email the reconciliation pack');
    } finally {
      setExporting(null);
    }
  };

  const downloadReport = async (format) => {
    if (!bankAccountId) return;
    setExporting(format);
    try {
      const res = await axios.get(`${API}/bank-reconciliation/${bankAccountId}/export`, {
        params: { format, as_at_date: asAtDate || undefined, from_date: exportFromDate || undefined }, responseType: 'blob',
      });
      const disposition = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(disposition);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = m ? m[1] : `reconciliation_${asAtDate || 'today'}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Reconciliation report downloaded (${format.toUpperCase()})`);
    } catch (e) {
      let msg = 'Could not download the report';
      try { msg = JSON.parse(await e?.response?.data?.text?.())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setExporting(null);
    }
  };
  const bankCcy = (data && (data.bank_currency || data.currency)) || 'USD';

  // Match dialog
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchCandidates, setMatchCandidates] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSourceItem, setMatchSourceItem] = useState(null);
  const [matchSide, setMatchSide] = useState('bank');

  // Record dialog
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [recordTransaction, setRecordTransaction] = useState(null);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Split dialog
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitTransaction, setSplitTransaction] = useState(null);

  // In-page document dialogs (bill / invoice / other payment / other receipt)
  const [docDialog, setDocDialog] = useState({ open: false, type: null, prefill: null });
  const [otherReceiptDialog, setOtherReceiptDialog] = useState({ open: false, prefill: null });
  const [otherPaymentDialog, setOtherPaymentDialog] = useState({ open: false, prefill: null });

  // Section collapse
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Initialize "As at" date to today
  useEffect(() => {
    const now = new Date();
    setAsAtDate(now.toISOString().split('T')[0]);
  }, []);

  const fetchReconciliation = useCallback(async () => {
    if (!bankAccountId || !asAtDate) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bank-reconciliation/${bankAccountId}`, {
        params: { as_at_date: asAtDate }
      });
      setData(res.data);
      if (res.data?.rounding_auto_absorbed) {
        const a = res.data.rounding_auto_absorbed;
        toast.info(`Bank-reported rounding of ${formatAmount(Math.abs(a.amount))} absorbed into ${a.rounding_gl} Rounding & Small Differences - books now equal the bank balance`);
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, asAtDate, onRefresh]);

  useEffect(() => { fetchReconciliation(); }, [fetchReconciliation]);

  const [correctingRounding, setCorrectingRounding] = useState(false);
  const correctRounding = async () => {
    setCorrectingRounding(true);
    try {
      const res = await axios.post(`${API}/bank-reconciliation/${bankAccountId}/correct-rounding`, { as_at_date: asAtDate });
      toast.success(`Books corrected to the bank balance (${formatAmount(Math.abs(res.data.corrected))} to ${res.data.rounding_gl} Rounding & Small Differences)`);
      fetchReconciliation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post the rounding correction');
    } finally {
      setCorrectingRounding(false);
    }
  };

  // ======================== MATCH FLOW ========================
  const openMatchDialog = async (item, side) => {
    setMatchSourceItem(item);
    setMatchSide(side);
    setMatchLoading(true);
    setShowMatchDialog(true);
    try {
      const amount = Math.abs(item.amount || item.bank_amount || 0);
      const direction = item.direction || (amount > 0 ? 'inflow' : 'outflow');
      const res = await axios.get(`${API}/bank-reconciliation/match-candidates/${bankAccountId}`, {
        params: { side, amount, direction }
      });
      setMatchCandidates(res.data.candidates || []);
    } catch (err) {
      toast.error('Failed to load match candidates');
      setMatchCandidates([]);
    } finally {
      setMatchLoading(false);
    }
  };

  const handleMatch = async (candidate) => {
    const bankTxId = matchSide === 'bank' ? matchSourceItem.id : candidate.bank_tx_id;
    const journalId = matchSide === 'bank' ? candidate.journal_id : matchSourceItem.journal_id;
    try {
      await axios.post(`${API}/bank-reconciliation/match`, {
        bank_tx_id: bankTxId, journal_id: journalId
      });
      toast.success('Matched successfully');
      setShowMatchDialog(false);
      fetchReconciliation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Match failed');
    }
  };

  // ======================== UNMATCH FLOW ========================
  const handleUnmatch = async (pair) => {
    try {
      await axios.post(`${API}/bank-reconciliation/unmatch`, {
        bank_tx_id: pair.bank_tx_id, journal_id: pair.journal_id
      });
      toast.success('Unmatched: bank transaction returned to Review Needed');
      fetchReconciliation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unmatch failed');
    }
  };

  // ======================== IN-PAGE DOCUMENT DIALOGS ========================
  // Bill / Invoice / Other Payment / Other Receipt open in place (no navigation)
  const openInPageDialog = (docType, prefillData) => {
    switch (docType) {
      case 'invoice':
      case 'bill':
        setDocDialog({ open: true, type: docType, prefill: prefillData });
        return true;
      case 'other_receipt':
        setOtherReceiptDialog({ open: true, prefill: prefillData });
        return true;
      case 'other_payment':
        setOtherPaymentDialog({ open: true, prefill: prefillData });
        return true;
      default:
        return false;
    }
  };

  const handleInPageDocSaved = () => {
    setDocDialog({ open: false, type: null, prefill: null });
    setOtherReceiptDialog({ open: false, prefill: null });
    setOtherPaymentDialog({ open: false, prefill: null });
    fetchReconciliation();
    onRefresh?.();
  };

  // ======================== RECORD FLOW (Not Quite Right) ========================
  const [linkTx, setLinkTx] = useState(null);
  const [taxPayTx, setTaxPayTx] = useState(null);

  const openRecordDialog = (item) => {
    setRecordTransaction(item);
    setShowRecordDialog(true);
  };

  const handleNavigateToRecord = (docType, tx) => {
    const prefillData = {
      amount: Math.abs(tx.amount),
      date: tx.date,
      description: tx.description,
      bank_feed_transaction_id: tx.id,
      bank_account_id: bankAccountId,
      currency: bankCcy,
      counterparty: tx.counterparty || tx.ai_counterparty,
      reference: tx.reference,
      source: 'reconciliation'
    };
    sessionStorage.setItem('bankFeedPrefill', JSON.stringify(prefillData));
    setShowRecordDialog(false);

    if (openInPageDialog(docType, prefillData)) return;
    if (docType === 'tax_payment') {
      setTaxPayTx({ ...tx, currency: tx.currency || bankCcy, bank_account_id: tx.bank_account_id || bankAccountId });
      return;
    }
    if (docType === 'link_existing') {
      setLinkTx({
        ...tx,
        direction: tx.direction || (tx.amount > 0 ? 'inflow' : 'outflow'),
        currency: tx.currency || bankCcy,
        bank_account_id: tx.bank_account_id || bankAccountId,
      });
      return;
    }
    toast.error('Invalid document type');
  };

  // ======================== DELETE FLOW ========================
  const openDeleteDialog = (item) => {
    setDeleteItem(item);
    setShowDeleteDialog(true);
  };

  const handleDelete = async (item) => {
    setDeleting(true);
    try {
      await axios.post(`${API}/bank-reconciliation/delete-gl-entry`, {
        journal_id: item.journal_id,
        reason: 'Reconciliation correction. Entry does not exist in bank'
      });
      toast.success('Entry deleted and journals reversed');
      setShowDeleteDialog(false);
      setDeleteItem(null);
      fetchReconciliation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ======================== SPLIT FLOW ========================
  const openSplitDialog = (item) => {
    setSplitTransaction({ ...item, bank_account_id: bankAccountId });
    setShowSplitDialog(true);
  };

  const handleSplitComplete = () => {
    setShowSplitDialog(false);
    setSplitTransaction(null);
    fetchReconciliation();
  };

  // Navigate to document creation for split transaction (mirrors BankFeedCenterPage's navigateToSplitDocumentCreation)
  const navigateToSplitDocumentCreation = (transaction, docType) => {
    const prefillData = {
      amount: Math.abs(transaction.amount),
      date: transaction.date,
      description: transaction.description,
      bank_feed_transaction_id: transaction.id,
      bank_account_id: bankAccountId,
      currency: bankCcy,
      counterparty: transaction.counterparty || transaction.ai_counterparty,
      reference: transaction.reference,
      source: 'reconciliation',
      split_transaction: true
    };
    sessionStorage.setItem('bankFeedPrefill', JSON.stringify(prefillData));
    setShowSplitDialog(false);
    setSplitTransaction(null);

    if (!openInPageDialog(docType, prefillData)) toast.error('Invalid document type');
  };

  // iter1094-final - Amount formatter with currency prefix. Falls back
  // in this order: (1) explicit override, (2) the reconciliation
  // statement's bank_currency, (3) the tab's bank_account currency,
  // (4) 'USD'. This ensures every line on the Reconciliation page
  // (Balance per Bank / Balance per GL / Difference / txn rows)
  // renders in the bank's OWN native currency - not the business
  // functional currency.
  const formatAmount = (val, ccyOverride) => formatCurrency(Math.abs(parseFloat(val) || 0), (ccyOverride || bankCcy || 'USD').toUpperCase());

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  // Handle bank accounts with no GL code yet
  if (data.gl_code_missing) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="reconciliation-no-gl">
        <AlertCircle className="w-10 h-10 text-amber-500 mb-3" />
        <h3 className="font-semibold text-lg mb-1">{data.account_name || data.bank_name}</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {data.message || 'This bank account has no linked GL code yet. Reconciliation will be available once transactions are synced and GL entries are created.'}
        </p>
      </div>
    );
  }

  const stmt = data.reconciliation_statement || {};
  const userMatched = data.user_matched_pairs || [];
  const suggested = data.suggested_matches || [];
  
  // New split: review_needed vs completed-but-unmatched
  const reviewNeeded = data.review_needed_transactions || { inflows: [], outflows: [] };
  const completedUnmatched = data.completed_unmatched_bank || { inflows: [], outflows: [] };
  const allReviewNeeded = [...(reviewNeeded.inflows || []), ...(reviewNeeded.outflows || [])];
  // iter1435 - Awaiting-bank-post is fully stripped from reconciliation.
  // The bank hasn't posted the tx yet, so it isn't an open reconciling
  // item. Backend excludes both the BFT and its linked journal on both
  // sides of the recon; the UI intentionally shows nothing here.
  const _allUnmatchedRaw = [...(completedUnmatched.inflows || []), ...(completedUnmatched.outflows || [])];
  const allCompletedUnmatched = _allUnmatchedRaw;
  
  // Legacy: in_bank_not_books still used for backward compat
  const bankNotBooks = data.in_bank_not_books || { inflows: [], outflows: [] };
  const booksNotBank = data.in_books_not_bank || { inflows: [], outflows: [] };
  const allBankNotBooks = [...(bankNotBooks.inflows || []), ...(bankNotBooks.outflows || [])];
  const allBooksNotBank = [...(booksNotBank.inflows || []), ...(booksNotBank.outflows || [])];

  const outstandingParts = [];
  if (allReviewNeeded.length) outstandingParts.push(`${allReviewNeeded.length} bank item${allReviewNeeded.length > 1 ? 's' : ''} to review`);
  if (allBooksNotBank.length) outstandingParts.push(`${allBooksNotBank.length} book entr${allBooksNotBank.length > 1 ? 'ies' : 'y'} awaiting bank`);
  if (allCompletedUnmatched.length) outstandingParts.push(`${allCompletedUnmatched.length} unlinked`);
  if (suggested.length) outstandingParts.push(`${suggested.length} suggested match${suggested.length > 1 ? 'es' : ''}`);
  const outstandingSummary = outstandingParts.length ? outstandingParts.join(' · ') : 'Nothing outstanding';
  const outstandingCount = allReviewNeeded.length + allBooksNotBank.length + allCompletedUnmatched.length;
  const unexplained = Math.abs(stmt.adjustment_needed || 0) >= 0.01;
  const fullyReconciled = !unexplained && outstandingCount === 0 && Math.abs(stmt.balance_difference || 0) < 0.01;

  const DATE_COL = { key: 'date', header: 'Date', className: 'whitespace-nowrap text-xs text-muted-foreground w-[110px]', render: (r) => formatDate(r.date) };
  const amountCol = () => ({ key: 'amount', header: 'Amount', className: 'text-right whitespace-nowrap w-[150px]', render: (r) => (
    <span className={`font-semibold tabular-nums ${r.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
      {r.direction === 'inflow' ? '+' : '-'}{formatAmount(r.amount)}
    </span>
  ) });
  const PairCell = ({ description, date, side, txId }) => (
    <div className="min-w-0">
      <p className="text-xs font-medium truncate max-w-[360px]" title={description}>{description}</p>
      <p className="text-[10px] text-muted-foreground flex items-center gap-2">{formatDate(date)} · {side}{txId && <BankTxActions transactionId={txId} compact />}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reconciliation-center">
      {/* Header · account identity + outstanding items only (mobile-first) */}
      <div className="space-y-2">
        {onClose && (
          <Button size="sm" variant="ghost" onClick={onClose} className="-ml-2 h-8 px-2 text-slate-600 hover:text-slate-900"
            data-testid="recon-back-to-summary-btn" aria-label="Back to all accounts">
            <ArrowLeft className="w-4 h-4 mr-1" /> All accounts
          </Button>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-base sm:text-lg leading-tight" data-testid="recon-account-label">
                {data.account_label || data.account_name || data.bank_name}
              </h3>
              {data.currency && <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid="recon-account-currency">{data.currency}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5" data-testid="recon-outstanding-summary">{outstandingSummary}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="as-at-date" className="text-xs text-muted-foreground whitespace-nowrap">As at</Label>
            <input type="date" id="as-at-date" value={asAtDate} onChange={(e) => setAsAtDate(e.target.value)}
              className="h-8 w-[9.5rem] text-sm border rounded-md px-2 bg-background" style={{ WebkitAppearance: 'none', fontSize: '0.8125rem' }}
              data-testid="recon-as-at-date" />
            <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={fetchReconciliation} data-testid="refresh-btn" aria-label="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={!!exporting} data-testid="recon-download-btn" aria-label="Download reconciliation report">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline text-xs">Download</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="px-2 py-1.5 space-y-1" onKeyDown={(e) => e.stopPropagation()}>
                  <Label htmlFor="recon-export-from" className="text-[11px] text-muted-foreground">Matched items from</Label>
                  <input type="date" id="recon-export-from" value={exportFromDate} max={asAtDate || undefined}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    className="h-8 w-full text-sm border rounded-md px-2 bg-background" data-testid="recon-export-from-date" />
                  <p className="text-[10px] text-muted-foreground">Default: start of the accounting year. <InfoTip title="What this filters" size="xs" testId="tip-recon-export-from">Only the matched-items listing is limited to this period. Balances are always as at the date above and outstanding items are always included in full.</InfoTip></p>
                </div>
                <DropdownMenuItem onClick={() => downloadReport('pdf')} data-testid="recon-download-pdf">
                  <FileText className="w-4 h-4 mr-2" /> Reconciliation report (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadReport('xlsx')} data-testid="recon-download-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Reconciliation report (Excel)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={emailReconPack} disabled={exporting === 'pack'} data-testid="recon-email-pack">
                  <Mail className="w-4 h-4 mr-2" /> Email last month's pack to the team
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Reconciliation Statement */}
      <Card>
        <CardContent className="pt-4">
          <div className="space-y-3 text-sm">
            {/* Balances */}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Balance per Bank Statement</span>
              <span className="text-foreground font-semibold" data-testid="recon-bank-balance">{formatAmount(stmt.bank_statement_balance)}</span>
            </div>
            {stmt.bank_balance_source && stmt.bank_balance_source !== 'computed' && (
              <p className="text-[10px] text-muted-foreground -mt-2 pl-1">Source: Connected bank</p>
            )}
            {stmt.bank_balance_source === 'computed' && (
              <p className="text-[10px] text-muted-foreground -mt-2 pl-1">Source: Opening balance + bank feed transactions</p>
            )}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Balance per General Ledger</span>
              <span className="text-foreground font-semibold" data-testid="recon-gl-balance">
                {(stmt.book_balance_gl || 0) < 0 ? `(${formatAmount(Math.abs(stmt.book_balance_gl))})` : formatAmount(stmt.book_balance_gl)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between items-center font-medium">
              <span>Difference</span>
              <span data-testid="recon-balance-diff">{formatAmount(Math.abs(stmt.balance_difference || 0))}</span>
            </div>
            
            {/* Unreconciled items breakdown */}
            <div className="bg-muted/40 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Explained by</p>
              
              {(stmt.bank_not_in_books_inflows > 0 || stmt.bank_not_in_books_outflows > 0) && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">In bank feed, not yet in books:</p>
                  {stmt.bank_not_in_books_inflows > 0 && (
                    <div className="flex justify-between pl-3">
                      <span className="text-muted-foreground">Receipts not yet recorded</span>
                      <span className="text-green-600">+{formatAmount(stmt.bank_not_in_books_inflows)}</span>
                    </div>
                  )}
                  {stmt.bank_not_in_books_outflows > 0 && (
                    <div className="flex justify-between pl-3">
                      <span className="text-muted-foreground">Payments not yet recorded</span>
                      <span className="text-red-600">-{formatAmount(stmt.bank_not_in_books_outflows)}</span>
                    </div>
                  )}
                </div>
              )}
              
              {(stmt.books_not_in_bank_inflows > 0 || stmt.books_not_in_bank_outflows > 0) && (() => {
                // iter 971f - split out inter-bank-transfer subtotals for
                // a clearer label ("In-transit inter-bank transfer") vs
                // the generic "book entry pending" bucket.
                const bnb = data.in_books_not_bank || { inflows: [], outflows: [] };
                const sumBy = (arr, fn) => arr.filter(fn).reduce((s, x) => s + (x.amount || 0), 0);
                const ibtIn = sumBy(bnb.inflows || [], (x) => x.is_inter_bank_transfer);
                const ibtOut = sumBy(bnb.outflows || [], (x) => x.is_inter_bank_transfer);
                const otherIn = (stmt.books_not_in_bank_inflows || 0) - ibtIn;
                const otherOut = (stmt.books_not_in_bank_outflows || 0) - ibtOut;
                return (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">In books, not in bank feed:</p>
                  {ibtIn > 0 && (
                    <div className="flex justify-between pl-3" data-testid="recon-in-transit-inflow">
                      <span className="text-sky-700 inline-flex items-center gap-1">
                        <Info className="w-3 h-3" />In-transit inter-bank transfer (awaiting bank feed)
                      </span>
                      <span className="text-green-600">+{formatAmount(ibtIn)}</span>
                    </div>
                  )}
                  {ibtOut > 0 && (
                    <div className="flex justify-between pl-3" data-testid="recon-in-transit-outflow">
                      <span className="text-sky-700 inline-flex items-center gap-1">
                        <Info className="w-3 h-3" />In-transit inter-bank transfer (awaiting bank feed)
                      </span>
                      <span className="text-red-600">-{formatAmount(ibtOut)}</span>
                    </div>
                  )}
                  {otherIn > 0 && (
                    <div className="flex justify-between pl-3">
                      <span className="text-muted-foreground">Book entries (receipts) pending</span>
                      <span className="text-green-600">+{formatAmount(otherIn)}</span>
                    </div>
                  )}
                  {otherOut > 0 && (
                    <div className="flex justify-between pl-3">
                      <span className="text-muted-foreground">Book entries (payments) pending</span>
                      <span className="text-red-600">-{formatAmount(otherOut)}</span>
                    </div>
                  )}
                </div>
                );
              })()}
              
              {stmt.bank_not_in_books_inflows === 0 && stmt.bank_not_in_books_outflows === 0 && 
               stmt.books_not_in_bank_inflows === 0 && stmt.books_not_in_bank_outflows === 0 && (
                <p className="text-xs text-muted-foreground italic pl-3">No unreconciled items</p>
              )}
              
              {/* Iter 681 - explicit explainers replace the misleading
                  "Opening balance adjustment" catchall. Each is drillable
                  and named so the user sees the actual cause of any gap. */}

              {/* iter1435 - Awaiting-bank-post is no longer surfaced as
                  an explainer. Backend fully excludes those rows on both
                  sides so they don't create a variance to explain. */}

              {stmt.opening_balance_difference != null && Math.abs(stmt.opening_balance_difference) >= 0.01 && stmt.opening_balance_difference_kind === 'unfetched_recent_activity' && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center" data-testid="recon-unfetched-activity">
                    <span className="text-amber-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Bank activity after {stmt.unfetched_since ? format(new Date(stmt.unfetched_since), 'd MMM yyyy') : 'the last fetched row'} not yet fetched
                    </span>
                    <span className="text-amber-700 font-medium">
                      {stmt.opening_balance_difference < 0 ? '-' : '+'}{formatAmount(Math.abs(stmt.opening_balance_difference))}
                    </span>
                  </div>
                  <p className="text-[10px] text-amber-600 pl-3">
                    Your bank reports {formatAmount(stmt.bank_statement_balance)} today, but the newest transaction your bank has sent us is dated {stmt.unfetched_since ? format(new Date(stmt.unfetched_since), 'd MMM') : 'earlier'}. The missing days arrive with the next bank update; your opening balance is unaffected.
                  </p>
                </>
              )}

              {stmt.opening_balance_difference != null && Math.abs(stmt.opening_balance_difference) >= 0.01 && stmt.opening_balance_difference_kind !== 'unfetched_recent_activity' && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center">
                    <span className="text-blue-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Opening balance not yet aligned with bank
                    </span>
                    <span className="text-blue-700 font-medium">{formatAmount(Math.abs(stmt.opening_balance_difference))}</span>
                  </div>
                  <p className="text-[10px] text-blue-600 pl-3">
                    Bank implies {formatAmount(stmt.bank_implied_opening_balance)}, books hold {formatAmount(stmt.book_opening_balance_total)}.
                    Usually the feed has not back-filled all history yet.
                  </p>
                </>
              )}

              {stmt.pending_at_bank_total != null && Math.abs(stmt.pending_at_bank_total) >= 0.01 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center" data-testid="recon-pending-at-bank">
                    <span className="text-sky-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Pending at bank, not yet posted
                    </span>
                    <span className="text-sky-700 font-medium">
                      {stmt.pending_at_bank_total < 0 ? '-' : '+'}{formatAmount(Math.abs(stmt.pending_at_bank_total))}
                    </span>
                  </div>
                  <p className="text-[10px] text-sky-600 pl-3">
                    Already off your bank balance, not yet posted. <InfoTip title="Pending at bank" size="xs" testId="tip-pending-at-bank">Your bank has deducted this from the balance it reports but has not posted the transaction yet. It is not a statement item; it flows into Review Needed once the bank posts it.</InfoTip>
                  </p>
                </>
              )}

              {stmt.orphan_reversal_book_total != null && Math.abs(stmt.orphan_reversal_book_total) >= 0.01 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center">
                    <span className="text-amber-700 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Orphan reversal journals on bank GL
                    </span>
                    <span className="text-amber-700 font-medium">{formatAmount(Math.abs(stmt.orphan_reversal_book_total))}</span>
                  </div>
                  <p className="text-[10px] text-amber-600 pl-3">
                    Reversals of already-voided entries. <InfoTip title="Orphan reversals" size="xs" testId="tip-orphan-reversals">Reversal journals on this bank account whose original entries have since been voided. Safe to delete from the Journals page if no longer needed.</InfoTip>
                  </p>
                </>
              )}

              {/* iter1407 · C3 · FX revaluation drift (foreign-currency
                  accounts only). Gap between last posted monthly reval
                  rate and today's rate applied to the current book bal. */}
              {stmt.fx_revaluation_drift != null && Math.abs(stmt.fx_revaluation_drift) >= 0.01 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center" data-testid="recon-fx-drift-explainer">
                    <span className="text-purple-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      FX revaluation drift
                    </span>
                    <span className="text-purple-700 font-medium">{formatAmount(Math.abs(stmt.fx_revaluation_drift))}</span>
                  </div>
                  <p className="text-[10px] text-purple-600 pl-3">
                    Rate moved since {stmt.fx_revaluation_last_reval_date || 'last month-end'} —
                    clears on the next automatic reval run.
                  </p>
                </>
              )}

              {/* iter1407 · C4 · Rounding drift (< 1 cent absorbed).
                  Only ever surfaces the auto-absorbed amount; if the
                  UI sees any it means a non-zero sub-cent artefact. */}
              {stmt.rounding_drift != null && Math.abs(stmt.rounding_drift) >= 0.005 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center" data-testid="recon-rounding-drift-explainer">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {stmt.provider_rounding_row ? 'Bank-reported balance rounding' : 'Rounding'}
                    </span>
                    <span className="text-slate-500 font-medium">{formatAmount(Math.abs(stmt.rounding_drift))}</span>
                  </div>
                  {stmt.provider_rounding_row && (
                    <div className="pl-3 space-y-1" data-testid="recon-provider-rounding-note">
                      <p className="text-[10px] text-slate-500">
                        Your transaction amounts are exact (2 dp). The running balances your bank reports through the connection wobble by ±0.01 on {stmt.provider_rounding_row.rows} row{stmt.provider_rounding_row.rows === 1 ? '' : 's'} (fractional-kobo interest/VAT rounding on the bank's side); their net effect is this amount, so the books are {stmt.rounding_drift < 0 ? 'above' : 'below'} the bank balance by {formatAmount(Math.abs(stmt.rounding_drift))}.
                      </p>
                      {(stmt.provider_rounding_row.examples || []).length > 0 && (
                        <ul className="text-[10px] text-slate-500 space-y-0.5" data-testid="recon-provider-rounding-rows">
                          {stmt.provider_rounding_row.examples.map((r) => (
                            <li key={r.external_id || r.raw_id} className="flex justify-between gap-2" data-testid={`recon-rounding-row-${r.external_id || r.raw_id}`}>
                              <span className="truncate">{r.date} · {r.narration || r.type}</span>
                              <span className="whitespace-nowrap">{formatAmount(r.amount)} vs bank {formatAmount(r.implied_amount)}</span>
                            </li>
                          ))}
                          {stmt.provider_rounding_row.rows > stmt.provider_rounding_row.examples.length && (
                            <li className="italic">+{stmt.provider_rounding_row.rows - stmt.provider_rounding_row.examples.length} more</li>
                          )}
                        </ul>
                      )}
                      {!data?.is_locked && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={correctingRounding}
                                onClick={correctRounding} data-testid="recon-correct-rounding-btn">
                          {correctingRounding ? 'Posting…' : `Correct to bank balance (${formatAmount(Math.abs(stmt.rounding_drift))})`}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* iter1450 · Named scope-gap buckets with their rows */}
              <ScopeGapExplainers scopeGaps={stmt.scope_gaps} formatAmount={formatAmount} />

              {/* iter1407 · Invariant banner — shown only when the
                  taxonomy has a hole. Every kobo should be assigned
                  to matched/open/explainer; anything else = mystery. */}
              {stmt.invariant_check && stmt.invariant_check.clean === false && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 mt-2 text-[11px] text-red-800 flex items-start gap-2"
                  data-testid="recon-invariant-banner">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Reconciliation taxonomy incomplete</div>
                    <div className="text-red-700 mt-0.5">
                      {formatAmount(Math.abs(stmt.invariant_check.discrepancy))} is unaccounted for.
                      Please raise a support ticket below so we can fix it.
                    </div>
                  </div>
                </div>
              )}

              {/* True residual - should be 0 after every explainer is accounted
                  for. Anything here means the engine still cannot account for
                  some kobo, which warrants investigation. */}
              {stmt.adjustment_needed != null && Math.abs(stmt.adjustment_needed) >= 0.01 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between pl-3 items-center">
                    <span className="text-rose-700 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Unexplained variance: investigate
                    </span>
                    <span className="text-rose-700 font-medium">{formatAmount(Math.abs(stmt.adjustment_needed))}</span>
                  </div>
                  <p className="text-[10px] text-rose-600 pl-3">
                    Unexplained residual — please contact support. <InfoTip title="Adjustment needed" size="xs" testId="tip-adjustment-needed">What is left after every known contributor has been accounted for. A non-zero value means an inconsistency the engine cannot classify; raise a support ticket so we can investigate this bank account.</InfoTip>
                  </p>
                  <div className="pl-3 pt-1">
                    <ReportIssueButton
                      label="Report this to AkountFly"
                      testId="recon-variance-report-btn"
                      fingerprint={`recon_variance:${bankAccountId}`}
                      issueContext={{
                        trigger: 'reconciliation_unexplained_variance',
                        bank_account_id: bankAccountId,
                        unexplained_amount: stmt.adjustment_needed,
                        balance_difference: stmt.balance_difference,
                        bank_statement_balance: stmt.bank_statement_balance,
                        book_balance_gl: stmt.book_balance_gl,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            
            <Separator />
            <div className={`p-2 rounded text-center text-sm font-semibold ${fullyReconciled ? 'bg-green-50 text-green-700' : unexplained ? 'bg-rose-50 text-rose-700' : 'bg-sky-50 text-sky-800'}`}
              data-testid="recon-status-banner">
              {fullyReconciled
                ? 'Reconciled'
                : unexplained
                  ? `Unexplained variance: ${formatAmount(Math.abs(stmt.adjustment_needed))}`
                  : `Difference of ${formatAmount(Math.abs(stmt.balance_difference || 0))} fully explained${outstandingCount ? ` · ${outstandingCount} outstanding item${outstandingCount > 1 ? 's' : ''}` : ''}`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ======= SUGGESTED MATCHES ======= */}
      {suggested.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('suggested')}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                Suggested Matches
                <Badge variant="secondary">{suggested.length}</Badge>
              </CardTitle>
              {collapsedSections.suggested ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
          </CardHeader>
          {!collapsedSections.suggested && (
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">May match journals you posted manually. Accept the ones that are right.</p>
              <ReconItemsTable
                testId="recon-suggested-grid"
                rows={suggested}
                rowTestId={(_, i) => `suggested-match-${i}`}
                columns={[
                  { key: 'actions', header: 'Actions', className: 'text-left whitespace-nowrap w-[1%]', render: (s, i) => (
                    <ReconActionButton label="Accept match" tone="approve" icon={Check} testId={`accept-suggested-${i}`}
                      onClick={() => handleMatch({ journal_id: s.journal_id, bank_tx_id: s.bank_tx_id })} />
                  ) },
                  { key: 'bank', header: 'Bank transaction', className: 'min-w-[220px]', render: (s) => (
                    <PairCell description={s.bank_description} date={s.bank_date} side="Bank" txId={s.bank_tx_id} />
                  ) },
                  { key: 'books', header: 'Entry in books', className: 'min-w-[220px]', render: (s) => (
                    <PairCell description={s.journal_description} date={s.journal_date} side="Books" />
                  ) },
                  { key: 'amount', header: 'Amount', className: 'text-right whitespace-nowrap w-[140px]', render: (s) => (
                    <div className="inline-flex flex-col items-end gap-0.5">
                      <span className="font-semibold tabular-nums">{formatAmount(s.bank_amount)}</span>
                      <Badge variant="outline" className="text-[9px]">{s.confidence}% match</Badge>
                    </div>
                  ) },
                ]}
              />
              {suggested.length > 1 && (
                <Button variant="outline" className="w-full" onClick={async () => {
                  try {
                    await axios.post(`${API}/bank-reconciliation/batch-match`, {
                      matches: suggested.map(s => ({ bank_tx_id: s.bank_tx_id, journal_id: s.journal_id }))
                    });
                    toast.success(`Accepted all ${suggested.length} suggested matches`);
                    fetchReconciliation();
                  } catch (err) { toast.error('Batch match failed'); }
                }} data-testid="accept-all-suggested">
                  <Check className="w-4 h-4 mr-1" /> Accept All {suggested.length} Suggestions
                </Button>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* ======= REVIEW NEEDED - In Bank Feed, Not Yet Recorded ======= */}
      {allReviewNeeded.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('reviewNeeded')}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="w-4 h-4 text-amber-600" />
                In Bank Feed, Review Needed
                <Badge variant="secondary">{allReviewNeeded.length}</Badge>
              </CardTitle>
              {collapsedSections.reviewNeeded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
            <CardDescription className="text-xs">Bank transactions not yet in your books. Link each one to a transaction you already recorded, or record / match / split it.</CardDescription>
          </CardHeader>
          {!collapsedSections.reviewNeeded && (
            <CardContent>
              <ReconItemsTable
                testId="recon-review-needed-grid"
                rows={allReviewNeeded}
                rowKey={(tx, i) => tx.id || i}
                rowTestId={(_, i) => `review-needed-${i}`}
                columns={[
                  { key: 'actions', header: 'Actions', className: 'text-left whitespace-nowrap w-[1%]', render: (tx, i) => (
                    <div className="flex items-center gap-1">
                      <ReconActionButton label="Link existing transaction" tone="link" icon={Link2} testId={`link-existing-review-${i}`}
                        title="Already recorded this invoice, bill, loan, asset or prepayment? Link this bank line to it"
                        onClick={() => handleNavigateToRecord('link_existing', tx)} />
                      <ReconActionButton label="Match book entry" icon={ArrowRightLeft} testId={`match-review-${i}`}
                        title="Match to a book entry of the exact same amount" onClick={() => openMatchDialog(tx, 'bank')} />
                      <ReconActionButton label="Record" icon={FileText} testId={`record-review-${i}`}
                        title="Record a new invoice, bill or other document" onClick={() => openRecordDialog(tx)} />
                      <ReconActionButton label="Split" icon={Scissors} testId={`split-review-${i}`}
                        title="Split into several documents" onClick={() => openSplitDialog(tx)} />
                    </div>
                  ) },
                  DATE_COL,
                  { key: 'desc', header: 'Description', className: 'min-w-[240px]', render: (tx) => (
                    <div className="flex items-center gap-2 min-w-0">
                      <DirectionPill direction={tx.direction} />
                      <span className="text-xs font-medium truncate max-w-[420px]" title={tx.description}>{tx.description}</span>
                      <BankTxActions transactionId={tx.id} compact />
                    </div>
                  ) },
                  amountCol(),
                ]}
              />
            </CardContent>
          )}
        </Card>
      )}

      {/* iter1435 - Awaiting-bank-post section removed. Bank hasn't
          posted the tx yet; nothing to reconcile until it does. Row
          rejoins the recon flow automatically on settlement. */}

      {/* ======= COMPLETED BUT UNLINKED - edge case ======= */}
      {allCompletedUnmatched.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('completedUnmatched')}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-500" />
                Processed but Unlinked
                <Badge variant="secondary">{allCompletedUnmatched.length}</Badge>
              </CardTitle>
              {collapsedSections.completedUnmatched ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
            <CardDescription className="text-xs">
              Posted to the ledger, but no matching book entry found. Match manually or reprocess.
            </CardDescription>
          </CardHeader>
          {!collapsedSections.completedUnmatched && (
            <CardContent>
              <ReconItemsTable
                testId="recon-unlinked-grid"
                rows={allCompletedUnmatched}
                rowKey={(tx, i) => tx.id || i}
                rowTestId={(_, i) => `completed-unmatched-${i}`}
                columns={[
                  { key: 'actions', header: 'Actions', className: 'text-left whitespace-nowrap w-[1%]', render: (tx, i) => (
                    <ReconActionButton label="Match book entry" tone="link" icon={ArrowRightLeft} testId={`match-unlinked-${i}`}
                      title="Match to a book entry of the exact same amount" onClick={() => openMatchDialog(tx, 'bank')} />
                  ) },
                  DATE_COL,
                  { key: 'desc', header: 'Description', className: 'min-w-[240px]', render: (tx) => (
                    <div className="flex items-center gap-2 min-w-0">
                      <DirectionPill direction={tx.direction} />
                      <span className="text-xs font-medium truncate max-w-[420px]" title={tx.description}>{tx.description}</span>
                      <BankTxActions transactionId={tx.id} compact />
                    </div>
                  ) },
                  amountCol(),
                ]}
              />
            </CardContent>
          )}
        </Card>
      )}

      {/* ======= POSTED IN BOOKS, NOT IN BANK FEED ======= */}
      {allBooksNotBank.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('booksNotBank')}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Posted in Books, Not in Bank Feed
                <Badge variant="secondary">{allBooksNotBank.length}</Badge>
              </CardTitle>
              {collapsedSections.booksNotBank ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
            <CardDescription className="text-xs">In your books, not yet on your bank feed — usually timing differences or manual entries.</CardDescription>
          </CardHeader>
          {!collapsedSections.booksNotBank && (
            <CardContent>
              <ReconItemsTable
                testId="recon-books-not-bank-grid"
                rows={allBooksNotBank}
                rowKey={(j, i) => j.journal_id || i}
                rowTestId={(_, i) => `books-not-bank-${i}`}
                columns={[
                  { key: 'actions', header: 'Actions', className: 'text-left whitespace-nowrap w-[1%]', render: (j, i) => (
                    <div className="flex items-center gap-1">
                      <ReconActionButton label="Match bank transaction" tone="link" icon={Link2} testId={`match-books-${i}`}
                        title="Match to a bank transaction of the exact same amount" onClick={() => openMatchDialog(j, 'books')} />
                      <ReconActionButton label="Delete" tone="danger" icon={Trash2} testId={`delete-books-${i}`}
                        title="Delete this entry and reverse its journals" onClick={() => openDeleteDialog(j)} />
                    </div>
                  ) },
                  DATE_COL,
                  { key: 'desc', header: 'Description', className: 'min-w-[240px]', render: (j, i) => (
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <DirectionPill direction={j.direction} />
                        <span className="text-xs font-medium truncate max-w-[420px]" title={j.description}>{j.description}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {j.source_type && <Badge variant="outline" className="text-[9px]">{j.source_type.replace(/_/g, ' ')}</Badge>}
                        {j.recorded_manually && (
                          <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-800 bg-amber-50" data-testid={`books-not-bank-manual-${i}`}>
                            recorded manually · awaiting bank line
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) },
                  amountCol(),
                ]}
              />
            </CardContent>
          )}
        </Card>
      )}

      {/* ======= USER-MATCHED PAIRS ======= */}
      {userMatched.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('matched')}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                Manually Matched
                <Badge variant="secondary">{userMatched.length}</Badge>
              </CardTitle>
              {collapsedSections.matched ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
            <CardDescription className="text-xs">Transactions you matched on this page. Unmatch to return the bank transaction to Review Needed.</CardDescription>
          </CardHeader>
          {!collapsedSections.matched && (
            <CardContent>
              <ReconItemsTable
                testId="recon-matched-grid"
                rows={userMatched}
                rowTestId={(_, i) => `user-matched-${i}`}
                columns={[
                  { key: 'actions', header: 'Actions', className: 'text-left whitespace-nowrap w-[1%]', render: (p, i) => (
                    <ReconActionButton label="Unmatch" tone="danger" icon={Unlink} testId={`unmatch-${i}`}
                      title="Unmatch: returns the bank transaction to Review Needed" onClick={() => handleUnmatch(p)} />
                  ) },
                  { key: 'bank', header: 'Bank transaction', className: 'min-w-[220px]', render: (p) => (
                    <PairCell description={p.bank_description} date={p.bank_date} side="Bank" txId={p.bank_tx_id} />
                  ) },
                  { key: 'books', header: 'Entry in books', className: 'min-w-[220px]', render: (p) => (
                    <PairCell description={p.journal_description} date={p.journal_date} side="Books" />
                  ) },
                  { key: 'amount', header: 'Amount', className: 'text-right whitespace-nowrap w-[140px] font-semibold tabular-nums', render: (p) => formatAmount(p.bank_amount) },
                ]}
              />
            </CardContent>
          )}
        </Card>
      )}

      {/* Empty state */}
      {allReviewNeeded.length === 0 && allCompletedUnmatched.length === 0 && allBooksNotBank.length === 0 && suggested.length === 0 && userMatched.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="font-medium">All Clear!</p>
          <p className="text-sm">No unmatched items for this period.</p>
        </div>
      )}

      {/* ======= DIALOGS ======= */}
      <MatchCandidateDialog
        currency={bankCcy}
        open={showMatchDialog}
        onClose={() => setShowMatchDialog(false)}
        candidates={matchCandidates}
        loading={matchLoading}
        sourceItem={matchSourceItem}
        onMatch={handleMatch}
        side={matchSide}
      />

      <RecordDocumentTypeDialog
        currency={bankCcy}
        open={showRecordDialog}
        onClose={() => setShowRecordDialog(false)}
        transaction={recordTransaction}
        onNavigate={handleNavigateToRecord}
      />
      <TaxPaymentDialog
        open={!!taxPayTx}
        onOpenChange={(o) => { if (!o) setTaxPayTx(null); }}
        transaction={taxPayTx}
        currency={bankCcy}
        onComplete={() => { setTaxPayTx(null); fetchReconciliation(); onRefresh?.(); }}
      />
      <SmartLinkDocumentModal
        open={!!linkTx}
        onOpenChange={(o) => { if (!o) setLinkTx(null); }}
        transaction={linkTx}
        onLinked={async () => { setLinkTx(null); fetchReconciliation(); onRefresh?.(); }}
      />

      <DeleteConfirmDialog
        currency={bankCcy}
        open={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setDeleteItem(null); }}
        item={deleteItem}
        onConfirm={handleDelete}
        loading={deleting}
      />

      {showSplitDialog && splitTransaction && (
        <SplitTransactionDialog
          open={showSplitDialog}
          onOpenChange={(v) => { if (!v) { setShowSplitDialog(false); setSplitTransaction(null); } }}
          transaction={splitTransaction}
          onNavigateToSplit={navigateToSplitDocumentCreation}
          formatAmount={formatAmount}
        />
      )}

      <BankFeedDocumentDialog
        open={docDialog.open}
        onOpenChange={(o) => setDocDialog((p) => ({ ...p, open: o }))}
        docType={docDialog.type}
        prefillData={docDialog.prefill}
        onSaved={handleInPageDocSaved}
      />
      <RecordOtherReceiptModal
        open={otherReceiptDialog.open}
        onOpenChange={(o) => setOtherReceiptDialog((p) => ({ ...p, open: o }))}
        prefilledData={otherReceiptDialog.prefill}
        onSuccess={handleInPageDocSaved}
      />
      <RecordOtherPaymentModal
        open={otherPaymentDialog.open}
        onOpenChange={(o) => setOtherPaymentDialog((p) => ({ ...p, open: o }))}
        prefilledData={otherPaymentDialog.prefill}
        onSuccess={handleInPageDocSaved}
      />
    </div>
  );
}
